import { useMemo } from 'react';
import {
  HydratedCallRecord,
  NetworkPerformance,
} from '@weave/schema-gen-ts/dist/schemas/phone-exp/phone-records/v1/call_record.pb';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess';
import { CallIntelConstant, CallIntelligenceV2, CallIntelQueries, CallIntelTypes } from '@frontend/api-call-intel';
import { getWeaveToken, hasSchemaACL } from '@frontend/auth-helpers';
import { callStatusUtils, useHasCallIntelAclAccess } from '@frontend/call-intel-core';
import appConfig from '@frontend/env';
import { useTranslation } from '@frontend/i18n';
import { useAppScopeStore, useScopedAppFlagStore } from '@frontend/scope';
import { useAlert } from '@frontend/design-system';
import { CallInsightPanelSource } from '../types';
import { getContactInfo, processTasks } from '../utils/call-insight-panel-util';

type Props = {
  callRecord: HydratedCallRecord;
  isDemoAccount?: boolean;
  source: CallInsightPanelSource;
};

export const useCallInsightPanelData = ({ callRecord, isDemoAccount, source }: Props) => {
  const { t } = useTranslation('call-insight-panel');
  const alert = useAlert();
  const { selectedLocationIds, accessibleLocationData, getLocationName } = useAppScopeStore();
  const { getFeatureFlagValue } = useScopedAppFlagStore();

  const contactInfo = useMemo(() => getContactInfo(callRecord), [callRecord]);
  const hasPrivateCommsFlag = getFeatureFlagValue('apply-phone-number-user-access-restriction');
  const hasCallIntelAccess = useHasCallIntelAclAccess([Permission.CALL_CLASSIFICATION_READ], {
    locations: [callRecord.locationId ?? ''],
  });
  const weaveToken = getWeaveToken();
  const locationId = callRecord.locationId || selectedLocationIds?.[0] || '';

  const { isTaskCreationEnabled: isTasksEnabled } = CallIntelligenceV2.Hooks.useAggregateTaskSettings({
    locationId,
    isDemo: isDemoAccount,
  });

  const { isFailedCall, isSkippedCall, isProcessingCall } = callStatusUtils.getCallFlags(callRecord.callIntelInsights);

  const callRecordingPath = useMemo(() => {
    if (!callRecord) return '';

    if (isDemoAccount) {
      return `${appConfig.BACKEND_API}/${CallIntelConstant.baseUrlV2}/demo/call-recording?token=${weaveToken}&location_id=${locationId}`;
    }

    if (hasPrivateCommsFlag) {
      return '';
    }

    if (callRecord.recordingFilename && callRecord.recordingPath) {
      return `${appConfig.BACKEND_API}/portal/v1/phone/callRecordings/${callRecord.channelId}?location_id=${locationId}&token=${weaveToken}`;
    }

    return '';
  }, [callRecord, hasPrivateCommsFlag, isDemoAccount, locationId, weaveToken]);

  const networkPerformance = useMemo(() => {
    const hasPerformanceIssue = callRecord.networkPerformance === NetworkPerformance.NETWORK_PERFORMANCE_ISSUE_DETECTED;
    const isPerformanceGood = callRecord.networkPerformance === NetworkPerformance.NETWORK_PERFORMANCE_GOOD;
    const shouldDisplay = hasPerformanceIssue || isPerformanceGood;
    return {
      hasPerformanceIssue,
      isPerformanceGood,
      performanceStatus: callRecord.networkPerformance,
      shouldDisplay,
      showQualityAccordion: shouldDisplay,
    };
  }, [callRecord.networkPerformance]);

  const hasAccesstoCallRecording = useMemo(() => {
    return selectedLocationIds.some((locationId) => {
      const candidate = accessibleLocationData[locationId];
      const hasPhoneFullAccessAcl =
        candidate?.locationType === 'unify'
          ? hasSchemaACL(candidate?.groupId, Permission.PHONE_FULL_ACCESS)
          : hasSchemaACL(candidate?.id, Permission.PHONE_FULL_ACCESS);

      return hasSchemaACL(locationId, Permission.CALL_RECORDING_READ) || hasPhoneFullAccessAcl;
    });
  }, [accessibleLocationData, selectedLocationIds]);

  const {
    data: callMetadataRes,
    isLoading: isLoadingCallMetadata,
    refetch: refetchCallMetadata,
  } = CallIntelQueries.useGetCallMetadataQuery({
    request: {
      id: callRecord.channelId ?? '',
      locationId: callRecord.locationId ?? '',
      isDemo: isDemoAccount,
      demoMetadata: isDemoAccount
        ? ({
            callRecord,
            locationName: getLocationName(locationId),
            invokedBy:
              source === 'callHistory'
                ? CallIntelTypes.CallMetadataRequestSource.PHONE_EVENTS
                : CallIntelTypes.CallMetadataRequestSource.INVALID_SOURCE,
          } as CallIntelTypes.GetCallMetadataIO['input']['demoMetadata'] & { callRecord: CallIntelTypes.CallRecord }) // callRecord is not present in contract but will be used by demo interceptor
        : undefined,
    },
    options: {
      enabled:
        !!callRecord.channelId &&
        !!callRecord.locationId &&
        !!callRecord.callIntelInsights &&
        !isFailedCall &&
        !isSkippedCall &&
        !isProcessingCall,
      onError: () => {
        alert.error(t("Couldn't fetch call metadata. Please try again."));
      },
      staleTime: 1000 * 60 * 5,
    },
  });

  const {
    data: taskDataRes,
    isLoading: isTaskFetching,
    queryKey: taskQueryKey,
  } = CallIntelQueries.useListTasks({
    payload: {
      locations: callRecord?.locationId ? [callRecord.locationId] : [],
      callIds: callRecord?.channelId ? [callRecord.channelId] : [],
      includeMetadata: true,
      isDemo: isDemoAccount,
      demoMetadata: isDemoAccount
        ? {
            callRecordList: [callRecord],
          }
        : undefined,
    },
    realQueryOptions: {
      cacheTime: 0,
      staleTime: 0,
      enabled:
        !!isTasksEnabled &&
        !!callRecord?.channelId &&
        !!callRecord?.locationId &&
        (callRecord.callIntelInsights?.taskCount || callRecord.taskCount || 0) > 0,
      onError: () => {
        alert.error(t('Failed to fetch call tasks'));
      },
    },
  });

  const callMetadata = useMemo<CallIntelTypes.CallMetadata>(() => {
    const transcript = callMetadataRes?.transcript ?? '';
    return {
      callParticipantsMetadata: {
        caller: callMetadataRes?.callMetadata?.caller ?? '',
        receptionist: callMetadataRes?.callMetadata?.receptionist ?? '',
      },
      rating: callMetadataRes?.rating,
      summary: callMetadataRes?.summary ?? '',
      transcript,
      tasks: processTasks(taskDataRes?.tasks, transcript),
    };
  }, [callMetadataRes, taskDataRes?.tasks]);

  return {
    callMetadata,
    callRecordingPath,
    contactInfo,
    hasCallIntelAccess,
    hasAccesstoCallRecording,
    isLoadingCallMetadata,
    isTaskFetching,
    networkPerformance,
    taskQueryKey,
    isTasksEnabled,
    refetchCallMetadata,
  };
};
